import type { FactOrigin } from "@ark-bot/protocol";

import { catalog } from "../i18n.js";

/** A bot's memory browser (components/Memory.tsx). The facts themselves are the bot's words and stay as written. */
export const MEMORY = catalog(
  {
    intro: (bot: string) => `What ${bot} has kept from your conversations. It reads these every turn, so a wrong one is worth forgetting: a forgotten fact is not re-learned unless you say it again.`,
    loading: "Loading…",
    forget: "Forget",
    forgetHint: "Forget this fact",
    aboutYou: "About you",
    aboutYouEmpty: "Nothing lasting about you yet.",
    log: "Log",
    logEmpty: "No dated facts yet.",
    shared: "Shared about you",
    sharedEmpty: "Nothing shared between bots yet.",
    forgotten: "Forgotten",
    hide: "Hide",
    show: (count: number) => `Show ${count}`,
    forgottenEmpty: "Nothing forgotten yet.",
    tidying: "Tidying",
    tidyNow: "Tidy now",
    tidyHint: "Merge duplicates and drop what a later fact supersedes; one pass, checked twice, undoable",
    tidyAbout: "A little after each conversation, at most once an hour, the bot merges duplicate facts and drops ones a later fact replaced. Every pass is checked by a second reader before it lands, and the last one can be undone here.",
    tidied: "Tidied.",
    nothingToTidy: "Nothing to tidy: the memory is fine as it is, or the audit said no.",
    lastTidy: (day: string, summary: string) => `Last tidy, ${day}: ${summary}`,
    undo: "Undo",
    clearTitle: (bot: string) => `Clear everything ${bot} remembers?`,
    clearMessage: "Its saved conversations go too. What other bots shared about you stays.",
    clearAction: "Clear memory",
    clearAll: "Clear all",
    done: "Done",
    learned: (by: FactOrigin, day: string) => `${{ bot: "Saved by the bot", extractor: "Picked up after a conversation", episode: "Journal of a conversation", tidy: "Merged by a tidy", user: "Restored by you" }[by]}, ${day}`,
    dropped: (by: FactOrigin, day: string) => `${{ bot: "Forgotten by the bot", extractor: "Dropped after a conversation", episode: "Dropped", tidy: "Dropped by a tidy", user: "Forgotten by you" }[by]}, ${day}`,
    fromMessage: (said: string) => `from your message “${said}”`,
    replaced: (facts: string) => `Replaced: ${facts}`,
    replacedBy: (fact: string) => `Replaced by: ${fact}`,
    restore: "Restore",
    restoreHint: "Bring this fact back; if a newer fact replaced it, that one is forgotten instead",
    archive: "Earlier conversations",
    archiveAbout: (count: number, since: string) =>
      count === 0
        ? "None kept yet. Every few exchanges, what was said is kept here so the bot can look it up when you mention something from before. It is never in the bot's prompt."
        : `${count} ${count === 1 ? "stretch" : "stretches"} of conversation since ${since}, kept so the bot can look them up when you mention something from before. They are never in its prompt, and Clear all removes them.`,
  },
  {
    intro: (bot: string) => `Hal-hal yang disimpan ${bot} dari percakapan kalian. Bot membacanya setiap giliran, jadi yang salah sebaiknya dilupakan: fakta yang dilupakan tidak dipelajari lagi kecuali kamu mengatakannya lagi.`,
    loading: "Memuat…",
    forget: "Lupakan",
    forgetHint: "Lupakan fakta ini",
    aboutYou: "Tentang kamu",
    aboutYouEmpty: "Belum ada yang diingat tentang kamu.",
    log: "Catatan",
    logEmpty: "Belum ada fakta bertanggal.",
    shared: "Dibagikan tentang kamu",
    sharedEmpty: "Belum ada yang dibagikan antarbot.",
    forgotten: "Dilupakan",
    hide: "Sembunyikan",
    show: (count: number) => `Tampilkan ${count}`,
    forgottenEmpty: "Belum ada yang dilupakan.",
    tidying: "Merapikan",
    tidyNow: "Rapikan sekarang",
    tidyHint: "Gabungkan fakta ganda dan buang yang sudah digantikan fakta lebih baru; sekali jalan, diperiksa dua kali, bisa diurungkan",
    tidyAbout: "Tak lama setelah tiap percakapan, paling sering sejam sekali, bot menggabungkan fakta ganda dan membuang yang sudah digantikan fakta lebih baru. Setiap perapian diperiksa pembaca kedua sebelum diterapkan, dan yang terakhir bisa diurungkan di sini.",
    tidied: "Sudah dirapikan.",
    nothingToTidy: "Tidak ada yang perlu dirapikan: memorinya sudah baik, atau pemeriksaannya tidak setuju.",
    lastTidy: (day: string, summary: string) => `Perapian terakhir, ${day}: ${summary}`,
    undo: "Urungkan",
    clearTitle: (bot: string) => `Hapus semua yang diingat ${bot}?`,
    clearMessage: "Percakapan yang disimpannya ikut terhapus. Yang dibagikan bot lain tentang kamu tetap ada.",
    clearAction: "Hapus memori",
    clearAll: "Hapus semua",
    done: "Selesai",
    learned: (by: FactOrigin, day: string) => `${{ bot: "Disimpan oleh bot", extractor: "Dicatat setelah percakapan", episode: "Jurnal percakapan", tidy: "Digabung saat perapian", user: "Dipulihkan oleh kamu" }[by]}, ${day}`,
    dropped: (by: FactOrigin, day: string) => `${{ bot: "Dilupakan oleh bot", extractor: "Dibuang setelah percakapan", episode: "Dibuang", tidy: "Dibuang saat perapian", user: "Dilupakan oleh kamu" }[by]}, ${day}`,
    fromMessage: (said: string) => `dari pesan kamu “${said}”`,
    replaced: (facts: string) => `Menggantikan: ${facts}`,
    replacedBy: (fact: string) => `Digantikan oleh: ${fact}`,
    restore: "Pulihkan",
    restoreHint: "Kembalikan fakta ini; kalau sudah digantikan fakta yang lebih baru, yang itu yang dilupakan",
    archive: "Percakapan sebelumnya",
    archiveAbout: (count: number, since: string) =>
      count === 0
        ? "Belum ada yang disimpan. Setiap beberapa obrolan, isi percakapannya disimpan di sini supaya bot bisa mencarinya saat kamu menyebut hal yang dulu. Ini tidak pernah masuk ke prompt bot."
        : `${count} potongan percakapan sejak ${since}, disimpan supaya bot bisa mencarinya saat kamu menyebut hal yang dulu. Ini tidak pernah masuk ke prompt bot, dan Hapus semua ikut menghapusnya.`,
  },
);
